/**
 * UserProfileContext — Wave A6 Chunk 2 (User Identity Layer).
 *
 * Fetches the authenticated user's cross-org identity row once and shares it
 * across TopBar, Sidebar, the avatar UserMenu, Settings, and CompleteProfile —
 * so the avatar/name render consistently without each surface re-fetching.
 *
 * The mutations (save profile, upload/remove avatar) live here too so every
 * consumer updates the same cached row and the UI stays in sync.
 */

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { apiGet, apiPatch, apiDelete } from '@kantera/api-client';
import { loadWebConfig } from '@kantera/config';
import type { ApiUserProfile, UpdateUserProfileRequest } from '@kantera/types';
import { useAuth } from '../hooks/useAuth';

const { apiUrl: API_URL } = loadWebConfig();

interface UserProfileContextType {
  profile: ApiUserProfile | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  updateProfile: (patch: UpdateUserProfileRequest) => Promise<ApiUserProfile>;
  uploadAvatar: (file: File) => Promise<string>;
  removeAvatar: () => Promise<void>;
}

const UserProfileContext = createContext<UserProfileContextType | undefined>(undefined);

export const UserProfileProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { accessToken, supabase } = useAuth();
  const [profile, setProfile] = useState<ApiUserProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!accessToken) {
      setProfile(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await apiGet<{ data: ApiUserProfile }>('/api/me/profile', {
        baseUrl: API_URL,
        token: accessToken,
      });
      setProfile(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const updateProfile = useCallback(
    async (patch: UpdateUserProfileRequest): Promise<ApiUserProfile> => {
      if (!accessToken) throw new Error('Not authenticated');
      const res = await apiPatch<UpdateUserProfileRequest, { data: ApiUserProfile }>(
        '/api/me/profile',
        patch,
        { baseUrl: API_URL, token: accessToken },
      );
      setProfile(res.data);
      // Refresh the Supabase session so the mirrored user_metadata.name flows
      // into the greeting / initials consumers that read user?.name.
      await supabase.auth.refreshSession().catch(() => undefined);
      return res.data;
    },
    [accessToken, supabase],
  );

  const uploadAvatar = useCallback(
    async (file: File): Promise<string> => {
      if (!accessToken) throw new Error('Not authenticated');
      const form = new FormData();
      form.append('file', file);
      const response = await globalThis.fetch(`${API_URL}/api/me/avatar`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
        body: form,
      });
      if (!response.ok) {
        const body = (await response.json().catch(() => ({}))) as { message?: string };
        throw new Error(body.message ?? `Upload failed (${response.status})`);
      }
      const json = (await response.json()) as { data: { avatarUrl: string } };
      setProfile((prev) => (prev ? { ...prev, avatarUrl: json.data.avatarUrl } : prev));
      return json.data.avatarUrl;
    },
    [accessToken],
  );

  const removeAvatar = useCallback(async () => {
    if (!accessToken) throw new Error('Not authenticated');
    await apiDelete('/api/me/avatar', { baseUrl: API_URL, token: accessToken });
    setProfile((prev) => (prev ? { ...prev, avatarUrl: null } : prev));
  }, [accessToken]);

  return (
    <UserProfileContext.Provider
      value={{ profile, loading, error, refetch, updateProfile, uploadAvatar, removeAvatar }}
    >
      {children}
    </UserProfileContext.Provider>
  );
};

export const useUserProfile = (): UserProfileContextType => {
  const ctx = useContext(UserProfileContext);
  if (!ctx) {
    throw new Error('useUserProfile must be used within UserProfileProvider');
  }
  return ctx;
};
